import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();

    // Clean up outside click listener
    if (this.#outsideClickController) {
      this.#outsideClickController.abort();
      this.#outsideClickController = null;
    }
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | TouchEvent} event
   */
  activate = (event) => {
    // Ignore synthetic touch pointer entry without disabling mouse or keyboard on hybrid devices.
    const isTouchPointer = event instanceof PointerEvent && event.pointerType === 'touch';
    if (event.type === 'pointerenter' && isTouchPointer) {
      return;
    }

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent | TouchEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu && isDefaultSlot) {
      const itemRect = item.getBoundingClientRect();
      const submenuRect = submenu.getBoundingClientRect();
      const inlineStart = Math.max(0, itemRect.left - submenuRect.left);
      submenu.style.setProperty('--submenu-inline-start', `${inlineStart}px`);
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');

    // Set up outside click listener for touch devices
    const isTouch = event instanceof TouchEvent || (event instanceof PointerEvent && event.pointerType === 'touch');
    if (isTouch) {
      // Remove any existing listener
      if (this.#outsideClickController) {
        this.#outsideClickController.abort();
      }

      // Add new listener after a small delay to avoid immediate triggering
      this.#outsideClickController = new AbortController();
      setTimeout(() => {
        if (this.#outsideClickController) {
          document.addEventListener('click', this.#handleOutsideClick, {
            signal: this.#outsideClickController.signal,
            capture: true,
          });
        }
      }, 0);
    }
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent | TouchEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item] - The item to deactivate (defaults to active item)
   * @param {boolean} [force=false] - Whether to force deactivation, bypassing hover checks
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;

    // Skip hover check if forcing deactivation
    if (!force && this.overflowHovered) return;

    // Clear any stuck focus/hover states when forcing
    if (force) {
      this.refs.overflowMenu?.blur();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }

    // Clean up outside click listener
    if (this.#outsideClickController) {
      this.#outsideClickController.abort();
      this.#outsideClickController = null;
    }

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Handles touchstart events on menu items.
   * On mobile, prevents navigation on first tap for items with submenus.
   * @param {TouchEvent} event - The touchstart event
   */
  handleTouchStart = (event) => {
    if (!(event.target instanceof Element)) return;

    // Find the menu item and check if it has a submenu
    const menuItem = findMenuItem(event.target);
    if (!menuItem) return;

    const hasSubmenu = menuItem.getAttribute('aria-haspopup') === 'true';
    const isMoreButton = event.target.slot === 'more';

    if (hasSubmenu || isMoreButton) {
      if (menuItem !== this.#state.activeItem) {
        // First tap: open submenu, prevent navigation
        event.preventDefault();
        this.activate(event);
      } else if (event.target.slot !== 'overflow') {
        // Already open: toggle closed
        event.preventDefault();
        this.#deactivate(this.#state.activeItem, true);
      }
    }
  };

  /**
   * Handles clicks outside the menu to close it.
   * Particularly useful for touch devices where users expect to close by tapping outside.
   * @param {MouseEvent | PointerEvent} event - The click event
   */
  #handleOutsideClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const clickedInsideMenu = this.contains(event.target);

    const clickedInsideOverflow =
      this.overflowMenu?.contains(event.target) || this.refs.overflowMenu?.contains(event.target);

    let clickedInsideSubmenu = false;
    if (this.#state.activeItem) {
      const activeSubmenu = findSubmenu(this.#state.activeItem);
      if (activeSubmenu) {
        clickedInsideSubmenu = activeSubmenu.contains(event.target);
      }
    }

    if (!clickedInsideMenu && !clickedInsideOverflow && !clickedInsideSubmenu) {
      this.#deactivate(this.#state.activeItem, true);
    }
  };

  /**
   * AbortController for managing outside click listeners
   * @type {AbortController | null}
   */
  #outsideClickController = null;

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  const closestItem = element.closest('[ref="menuitem"]');
  if (closestItem instanceof HTMLElement) return closestItem;

  const childItem = element.querySelector('[ref="menuitem"]');
  return childItem instanceof HTMLElement ? childItem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
